'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { Button } from '@govinity/ui/components/ui/button';
import { LoadingSpinner } from '@govinity/ui/components/ui/loading-spinner';

/**
 * Holds a viewer on the waiting panel until the translated file is ready: the file on hand is the
 * untranslated one and its signed URL is reissued on every poll, so rendering it would reload the
 * viewer every few seconds only to replace it. Release is keyed by document, so escaping one
 * wait does not skip the next document's.
 */
export function useTranslationHold(documentId: number | null, isTranslating: boolean) {
  const [releasedId, setReleasedId] = useState<number | null>(null);

  return {
    isHeld: isTranslating && releasedId !== documentId,
    release: () => setReleasedId(documentId),
  };
}

type DocumentTranslationPendingProps = {
  /** Escapes the wait and shows the untranslated file — a long packet can take minutes. */
  onViewOriginal: () => void;
};

export function DocumentTranslationPending({ onViewOriginal }: DocumentTranslationPendingProps) {
  const t = useTranslations('DocumentTranslationPending');

  return (
    <output
      aria-live="polite"
      className="flex flex-col items-center justify-center py-12 text-center"
    >
      <LoadingSpinner size={40} className="mb-4" aria-hidden="true" />
      <h2 className="text-foreground mb-2 text-lg font-semibold">{t('title')}</h2>
      <p className="text-muted-foreground mb-2 max-w-md text-sm">{t('description')}</p>
      <p className="text-muted-foreground mb-4 max-w-md text-xs">{t('disclaimer')}</p>
      <Button onClick={onViewOriginal} size="sm" variant="outline">
        {t('viewOriginal')}
      </Button>
    </output>
  );
}
